import { Injectable } from '@angular/core';
import { Analise, TipoAnalise } from 'src/app/dashboard/models/analise';
import { ColorType } from 'src/app/instant-analysis/models/color-type';
import { HeatmapType } from 'src/app/instant-analysis/models/heatmap-type';
import { FilterConfig, GeometryData, LayerConfig, LayerItem, 
  MapConfig, MapConfigDetails, TimeConfig } from 'src/app/instant-analysis/models/map-config';
import { TimeFormatterService } from 'src/app/shared/util/time-formatter';

/**
 * O objetivo deste serviço é centralizar a configuração do mapa, facilitando a
 * transmissão dos dados da configuração entre os vários componentes que a usam.
 */
@Injectable({
  providedIn: 'root'
})
export class MapConfigService implements MapConfigService {

  /** Referência global à configuração do mapa. */
  instantMapConfig: MapConfig;

  /**  */
  intervalMapConfig: MapConfig;

  constructor(private timeFormatterService: TimeFormatterService) {
    this.instantMapConfig = this.instant();
    this.intervalMapConfig = this.interval();
  }

  /**
   * Cria instância de MapConfig em um estado inicial, para a tela da análise instantânea.
   * @returns instância do MapConfig com valores iniciais vazios.
   */
  instant() {

    let [startDate, startTime] = this.timeFormatterService.dateParts(new Date());
    let [endDate, endTime] = this.timeFormatterService.dateParts(new Date());

    let layerList: LayerItem[] = this.createInstantAnalysisLayerList();
    let dataSourceOptions = this.createDataSourceOptions();
  
    return <MapConfig> {
      routed: false,
      details: new MapConfigDetails(null, null, TipoAnalise.INSTANTANEA, null),
      time: new TimeConfig(false, false, [], startDate, startTime, endDate, endTime, 24, 0),
      layer: new LayerConfig(0.1, [10, 90], layerList),
      geoData: new GeometryData('quinzemin', null, null, null, []),
      dataFilter: new FilterConfig(dataSourceOptions, 0),
    };
  }

  /**
   * Cria instância de MapConfig para a análise intervalar.
   * @returns instância de MapConfig intervalar.
   */
  interval() {
    const datetimeNow = new Date();
    let datetimeLastMonth = new Date(datetimeNow.getFullYear(), datetimeNow.getMonth() - 1, datetimeNow.getDate());
    datetimeLastMonth.setHours(datetimeNow.getHours(), datetimeNow.getMinutes(), 0);

    let [startDate, startTime] = this.timeFormatterService.dateParts(datetimeLastMonth);
    let [endDate, endTime] = this.timeFormatterService.dateParts(datetimeNow);

    let layerList: LayerItem[] = this.createIntervalAnalysisLayerList();
    let dataSourceOptions = this.createDataSourceOptions();

    return <MapConfig> {
      routed: false,
      details: new MapConfigDetails(null, null, TipoAnalise.HISTORICA, null),
      time: new TimeConfig(false, false, [], startDate, startTime, endDate, endTime, 24, 0),
      layer: new LayerConfig(0.1, [10, 90], layerList),
      geoData: new GeometryData('total', null, null, null, []),
      dataFilter: new FilterConfig(dataSourceOptions, 0),
    };
  }

  /**
   * Cria uma instância de MapConfig a partir de uma Analise instantânea ou intervalar,
   * e a atribui ao atributo correspondente no serviço.
   * @param analise análise salva previamente, que será restaurada no MapConfig.
   * @returns instância de MapConfig.
   */
  setMapConfigFromAnalise(analise: Analise) {

    let mapConfig: MapConfig = this.fromAnalise(analise);
    mapConfig.routed = true;

    if (analise.tipo == TipoAnalise.INSTANTANEA) {
      this.instantMapConfig = mapConfig;
    }
    else if (analise.tipo == TipoAnalise.HISTORICA) {
      this.intervalMapConfig = mapConfig;
    }
  }

  /**
   * Cria instância de MapConfig a partir de uma análise intervalar ou instantânea.
   * @param analise análise a ser restaurada no MapConfig.
   * @returns instância MapConfig.
   */
  private fromAnalise(analise: Analise) {
    return <MapConfig> {
      details: new MapConfigDetails(analise.id, analise.id_projeto, analise.tipo, 
        analise.nome),
      time: new TimeConfig(false, true, [], analise.data_inicio, analise.hora_inicio,
        analise.data_fim, analise.hora_fim, analise.intervalo_horas_ocorrencias,
        analise.intervalo_minutos_ocorrencias),
      layer: new LayerConfig(analise.tilesize, [analise.percentil_min, 
        analise.percentil_max], analise.camadas),
      geoData: new GeometryData(analise.coluna_selecionada, null, null, null, analise.regioes),
      dataFilter: new FilterConfig(this.createDataSourceOptions(), 
        analise.porcentagem_completude),
    };
  }

  /**
   * Cria uma análise a partir de uma MapConfig.
   * @param mapConfig MapConfig usada para criar Analise
   * @returns instância de Analise.
   */
  createAnalysis(mapConfig: MapConfig) {

    let { details, time, layer, geoData, dataFilter } = mapConfig;

    return <Analise> {
      // Dados de Identificação da análise
      id: details.analysisId,
      id_projeto: details.projectId,
      tipo: details.analysisType,
      nome : details.analysisName,

      // Dados de tempo da análise
      data_inicio: time.startDate,
      hora_inicio: time.startTime,
      data_fim: time.endDate,
      hora_fim: time.endTime,
      intervalo_horas_ocorrencias: time.eventIntervalInHours,    
      intervalo_minutos_ocorrencias: time.eventIntervalInMinutes,
      
      // Dados de camadas da análise
      tilesize: layer.tilesizeInKm,
      percentil_min: layer.percentile[0],
      percentil_max: layer.percentile[1],
      camadas: layer.list,

      // Dados de geometria
      coluna_selecionada: geoData.selectedColumn,
      regioes: geoData.polygons,

      // Dados do filtro de dados
      porcentagem_completude: dataFilter.completeness,
      fonte_prefeitura_niteroi: dataFilter.dataSource.includes('PREFEITURA NITERÓI'),
      fonte_cemaden: dataFilter.dataSource.includes('CEMADEN'),
    };
  }

  /**
   * Cria array de objetos de opções de fontes de dados para um formulário.
   * @returns array de opções.
   */
  private createDataSourceOptions() {
    return [
      { 'text': 'Todas', 'value': ['PREFEITURA NITERÓI', 'CEMADEN'] },
      { 'text': 'Prefeitura de Niterói', 'value': ['PREFEITURA NITERÓI'] },
      { 'text': 'CEMADEN', 'value': ['CEMADEN'] },
    ];
  }

  /**
   * Cria um array de configurações das camadas do mapa para uma análise instantânea.
   * @returns array de configurações das camadas.
   */
  private createInstantAnalysisLayerList() {

    const { DISCRETE, CONTINUOUS, CONSTANT } = ColorType;

    return <LayerItem[]> [
      { type: HeatmapType.STATIONS, isActive: false, color: DISCRETE },
      { type: HeatmapType.HEATMAP, isActive: false, color: CONTINUOUS },
      { type: HeatmapType.HIGHWAYS, isActive: true, color: DISCRETE },
      { type: HeatmapType.RIVERS, isActive: false, color: DISCRETE },
      { type: HeatmapType.NEIGHBORHOOD, isActive: false, color: DISCRETE },
      { type: HeatmapType.VORONOI, isActive: false, color: CONTINUOUS },
      { type: HeatmapType.EVENTS, isActive: false, color: CONSTANT },
      { type: HeatmapType.CAMERA, isActive: false, color: CONSTANT },
      { type: HeatmapType.EVENTS_HEATMAP, isActive: false, color: CONTINUOUS },
    ];
  }

  /**
   * Cria um array de configurações das camadas do mapa para uma análise intervalar.
   * @returns array de configurações das camadas.
   */
  private createIntervalAnalysisLayerList() {

    const { DISCRETE, CONTINUOUS, CONSTANT } = ColorType;

    return <LayerItem[]> [
      { type: HeatmapType.STATIONS, isActive: false, color: CONTINUOUS },
      { type: HeatmapType.HEATMAP, isActive: false, color: CONTINUOUS },
      { type: HeatmapType.HIGHWAYS, isActive: true, color: CONTINUOUS },
      { type: HeatmapType.RIVERS, isActive: false, color: CONTINUOUS },
      { type: HeatmapType.NEIGHBORHOOD, isActive: false, color: CONTINUOUS },
      { type: HeatmapType.VORONOI, isActive: false, color: CONTINUOUS },
      { type: HeatmapType.EVENTS, isActive: false, color: CONSTANT },
      { type: HeatmapType.CAMERA, isActive: false, color: CONSTANT },
      { type: HeatmapType.EVENTS_HEATMAP, isActive: false, color: CONTINUOUS },
    ];
  }
}
